import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Stepper, Step, StepLabel, StepContent,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PublishIcon from '@mui/icons-material/Publish';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';
import AgentReviewDialog from './AgentReviewDialog';
import { isDmsAdmin } from '../utils/dmsPermissions';

export default function DocumentWorkflowPanel({ document, user, onDocumentUpdate }) {
  const MP = useDmsPalette();
  const STEP_STATUS_COLOR = {
    pending: MP.yellow,
    approved: MP.green,
    rejected: '#ef4444',
  };
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [preReview, setPreReview] = useState(null);
  const [preReviewOpen, setPreReviewOpen] = useState(false);
  const [publishReview, setPublishReview] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  const activeWorkflow = workflows.find((w) => w.status === 'active');
  const docId = document?._id;

  const loadWorkflows = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const data = await dmsApi.listWorkflows({ documentId: docId });
      setWorkflows(Array.isArray(data) ? data : data.workflows || []);
    } catch (err) {
      setError(err.error || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await dmsApi.initiateWorkflow(docId, { type: 'publish_approval' });
      if (res.preReview) {
        setPreReview(res.preReview);
        setPreReviewOpen(true);
      }
      await loadWorkflows();
      onDocumentUpdate?.();
    } catch (err) {
      setError(err.error || 'Failed to submit for approval');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (forcePublish = false) => {
    setPublishing(true);
    setError(null);
    try {
      const res = await dmsApi.publishDocument(docId, { forcePublish });
      setReviewDialogOpen(false);
      setPublishReview(null);
      onDocumentUpdate?.(res.document);
    } catch (err) {
      if (err.publishReview) {
        setPublishReview(err.publishReview);
        setReviewDialogOpen(true);
      } else {
        setError(err.error || 'Publish failed');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleCancelWorkflow = async () => {
    if (!activeWorkflow) return;
    setSubmitting(true);
    try {
      await dmsApi.cancelWorkflow(activeWorkflow._id);
      await loadWorkflows();
      onDocumentUpdate?.();
    } catch (err) {
      setError(err.error || 'Failed to cancel workflow');
    } finally {
      setSubmitting(false);
    }
  };

  if (!document) return null;
  if (document.status === 'published') {
    return (
      <Box sx={{ p: 2, bgcolor: `${MP.green}15`, borderRadius: 1, border: `1px solid ${MP.green}44` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: MP.green }} />
          <Typography variant="body2" fontWeight={600}>Published</Typography>
        </Box>
      </Box>
    );
  }

  const canPublishDirect = isDmsAdmin(user) && document.status === 'draft' && !activeWorkflow;

  return (
    <Box sx={{ p: 2, bgcolor: MP.surface, borderRadius: 1, border: `1px solid ${MP.border}` }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: MP.textSecondary }}>
        Approval & Publishing
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <CircularProgress size={24} />
      ) : activeWorkflow ? (
        <Box>
          <Chip label={`Workflow: ${activeWorkflow.type}`} size="small" sx={{ mb: 1.5, textTransform: 'capitalize' }} />
          <Stepper activeStep={activeWorkflow.currentStep} orientation="vertical" sx={{ mb: 1 }}>
            {activeWorkflow.steps.map((step, idx) => (
              <Step key={idx} completed={step.status === 'approved'}>
                <StepLabel
                  optional={
                    step.actedAt ? (
                      <Typography variant="caption">{format(new Date(step.actedAt), 'MMM d, h:mm a')}</Typography>
                    ) : null
                  }
                  error={step.status === 'rejected'}
                >
                  {step.name}
                  {step.assigneeId?.name && ` — ${step.assigneeId.name}`}
                </StepLabel>
                <StepContent>
                  <Chip
                    label={step.status}
                    size="small"
                    sx={{
                      bgcolor: `${STEP_STATUS_COLOR[step.status] || MP.textSecondary}22`,
                      color: STEP_STATUS_COLOR[step.status] || MP.textSecondary,
                      textTransform: 'capitalize',
                    }}
                  />
                  {step.comment && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{step.comment}</Typography>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
          <Button
            size="small"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={handleCancelWorkflow}
            disabled={submitting}
          >
            Cancel Workflow
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {document.status === 'draft'
              ? 'Submit this document for human approval before publishing.'
              : 'No active workflow.'}
          </Typography>
          {document.status === 'draft' && (
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSubmitForApproval}
              disabled={submitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              {submitting ? 'Submitting…' : 'Submit for Approval'}
            </Button>
          )}
          {canPublishDirect && (
            <Button
              variant="outlined"
              startIcon={<PublishIcon />}
              onClick={() => handlePublish(false)}
              disabled={publishing}
              sx={{ alignSelf: 'flex-start', borderColor: MP.green, color: MP.green }}
            >
              {publishing ? 'Publishing…' : 'Publish (Admin)'}
            </Button>
          )}
        </Box>
      )}

      <Dialog open={preReviewOpen} onClose={() => setPreReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pre-Review Advisory</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Agent pre-review score: <strong>{preReview?.overallScore}/100</strong>
          </Typography>
          {(preReview?.blockers || []).map((b, i) => (
            <Alert key={i} severity="warning" sx={{ mb: 1 }}>{b.message}</Alert>
          ))}
          <Typography variant="caption" color="text.secondary">
            Workflow started. Reviewers will be notified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreReviewOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <AgentReviewDialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        review={publishReview}
        onForcePublish={isDmsAdmin(user) ? () => handlePublish(true) : null}
        publishing={publishing}
      />
    </Box>
  );
}